import type { AccountRole, Role } from './auth'

export interface NavItem {
  to: string
  label: string
  roles: Role[]
  icon: string          // inline SVG path data, so there is no icon dependency
  section: string
  module: ModuleKey
  /** Only these account roles (practice manager, CEO) as well as the role: the Team module. */
  accountRoles?: AccountRole[]
  /** Also needs this module to be bought: a page shown inside another module (Who buys what, in Inventory). */
  needs?: ModuleKey
}

/**
 * A MODULE is a whole product inside Lynqpod, not a section of one. Everything built
 * so far — paying practitioners, the practice's own money, and the settings behind
 * both — is one module: Finance. It is deliberately not split three ways; running
 * payroll and chasing a debtor are the same person's evening, and making them
 * switch between panes to do it would be work invented for its own sake.
 *
 * The switcher exists for the NEXT module, whatever that turns out to be. Until
 * there is one it renders as a plain badge: a chooser with a single option is
 * furniture, so it does not open.
 *
 * A practitioner has their own, and sees only that.
 */
export type ModuleKey = 'finance' | 'appointments' | 'rota' | 'marketing' | 'reports' | 'inventory' | 'automations' | 'chat' | 'research' | 'recordings' | 'team' | 'whatsapp' | 'mine' | 'platform'

export interface ModuleDef {
  key: ModuleKey
  label: string
  blurb: string
  /** Two letters for the badge where the logo used to sit. */
  mark: string
  roles: Role[]
  /** Only these account roles too (both ride on data_admin; see utils/accountRole.js on the server). */
  accountRoles?: AccountRole[]
}

export const MODULES: ModuleDef[] = [
  /*
   * THE ORDER HERE IS THE ORDER EVERYWHERE: the module switcher, the sidebar and the
   * keyboard order all read this list. Appointments, Marketing, Finance, then the rest,
   * which is the order of a practice's day: fill the chairs, find the people who fill
   * them, then count what it came to. It used to open on Finance, which is the one of
   * the three that nobody starts their morning in.
   */
  { key: 'appointments', label: 'Appointments', mark: 'Ap', roles: ['admin', 'data_admin'],
    blurb: 'The diary, no-shows and how full the chairs are' },
  // Its own module rather than a page under Appointments: judging what advertising
  // costs is a different job, done by a different person, on a different rhythm to
  // filling tomorrow's diary.
  { key: 'marketing',    label: 'Marketing',    mark: 'Mk', roles: ['admin', 'data_admin'],
    blurb: 'Where patients come from, and what they cost' },
  /*
   * ⚠️ READING IS NOT THE SAME JOB AS SETTING UP. Marketing is where somebody builds an
   * advert, writes a form, designs the booking page. Reports is where somebody else, usually
   * on a different day, asks what any of it produced. They were one module and the reading
   * half kept being found by accident, underneath the doing half.
   *
   * It has no entitlement of its own on the server; a practice that has Marketing has this.
   */
  { key: 'reports',      label: 'Reports',      mark: 'Rp', roles: ['admin', 'data_admin'],
    blurb: 'What every source actually produced, and what it cost' },
  { key: 'finance', label: 'Finance', mark: 'F', roles: ['admin', 'data_admin'],
    blurb: 'Payroll, the practice ledger and the settings behind both' },
  { key: 'rota',         label: 'Rota',         mark: 'Ro', roles: ['admin', 'data_admin'],
    blurb: 'Who is in which room, with which nurse' },
  /**
   * The patient portal: what they ask for through it, and what they are shown in it.
   *
   * The pre-visit form used to live here too and has moved to Appointments, because it
   * is sent off the back of a booking by the person who just made it. What is left is
   * the portal, which is a different rhythm: a queue somebody works through, and a list
   * somebody edits when prices change.
   */
  /**
   * What is on the shelf. Its own module rather than a page under Patients, because the
   * portal's price list and the stock cupboard are two different jobs: one is what a
   * patient is shown and charged, the other is how many are left and when to order more.
   * They meet at exactly one point — a collected order takes one off the shelf — and
   * that is a link, not a reason to make them the same screen.
   */
  /**
   * The clinicians, one by one: what was collected for their work, their diary and the
   * sundries sold to their patients. Practice managers and CEOs only (server:
   * routes/team.routes.js checks the account too).
   */
  { key: 'team',         label: 'Team',         mark: 'Tm', roles: ['data_admin'], accountRoles: ['practice_manager', 'ceo'],
    blurb: 'Each clinician: what they made, their diary and what they sold' },
  { key: 'inventory',    label: 'Inventory',    mark: 'In', roles: ['admin', 'data_admin'],
    blurb: 'Sundries on the shelf, what has gone and what to reorder' },
  /**
   * Rules the practice writes for itself. Its own module, not a settings page, because
   * it is used every day: the inbox is where the to-dos it hands out are worked through.
   */
  { key: 'automations',  label: 'Automations',  mark: 'Au', roles: ['admin', 'data_admin'],
    blurb: 'When something happens, tell someone or give them a to-do' },
  /** Patients talking to the practice (the bot first), the team, and the assistant. */
  { key: 'chat',         label: 'Chat',         mark: 'Ch', roles: ['admin', 'data_admin'],
    blurb: 'Patient chats, WhatsApp, team messages and the assistant' },
  // Its own module: finding patterns is a different job from sending to them.
  { key: 'research',     label: 'Research',     mark: 'Rs', roles: ['admin', 'data_admin'],
    blurb: 'Who buys what: the patients and treatments behind your sales' },
  // Watching how visitors use the practice's website and booking page. Sold on its own.
  { key: 'recordings',   label: 'Visitors',     mark: 'Vi', roles: ['admin', 'data_admin'],
    blurb: 'Watch how visitors use your website and booking page' },
  // Not a module of the product. The product's own console, for whoever runs it.
  { key: 'platform',     label: 'Platform',     mark: 'Lq', roles: ['platform_admin'],
    blurb: 'Every practice on Lynqpod' },
  { key: 'mine',    label: 'Mine',    mark: 'My', roles: ['practitioner'],
    blurb: 'Your payslips, your details and your diary' },
]

// Mirrors the route guards in dist/routes/*. If you change one, change the other:
// a link the API will refuse is worse than no link.
const ICON = {
  home:    'M3 10.5 12 3l9 7.5M5.25 9.75V21h13.5V9.75',
  chart:   'M3 3v18h18M8 17V9m4 8V5m4 12v-6',
  invoice: 'M8 6h8M8 10h8M8 14h5M6 3h12a1 1 0 0 1 1 1v16l-3-2-2 2-2-2-2 2-3-2V4a1 1 0 0 1 1-1Z',
  upload:  'M12 16V4m0 0L8 8m4-4 4 4M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2',
  card:    'M3 7a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7Zm0 3h18',
  percent: 'M19 5 5 19M8.5 8.5a2 2 0 1 1-3 0 2 2 0 0 1 3 0Zm10 10a2 2 0 1 1-3 0 2 2 0 0 1 3 0Z',
  sliders: 'M4 6h16M4 12h16M4 18h16M9 4v4M15 10v4M7 16v4',
  users:   'M16 19v-2a3 3 0 0 0-3-3H6a3 3 0 0 0-3 3v2M12.5 7.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0ZM21 19v-2a3 3 0 0 0-2.25-2.9M15.75 4.6a3 3 0 0 1 0 5.8',
  cog:     'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm8-3a8 8 0 0 1-.1 1.2l2 1.6-2 3.4-2.4-1a8 8 0 0 1-2 1.2l-.4 2.6h-4l-.4-2.6a8 8 0 0 1-2-1.2l-2.4 1-2-3.4 2-1.6A8 8 0 0 1 4 12c0-.4 0-.8.1-1.2l-2-1.6 2-3.4 2.4 1a8 8 0 0 1 2-1.2L8.9 3h4l.4 2.6a8 8 0 0 1 2 1.2l2.4-1 2 3.4-2 1.6c.1.4.1.8.1 1.2Z',
  user:    'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm7 8v-1a5 5 0 0 0-5-5h-4a5 5 0 0 0-5 5v1',
  receipt: 'M6 3h12a1 1 0 0 1 1 1v17l-2.5-1.5L14 21l-2-1.5L10 21l-2.5-1.5L5 21V4a1 1 0 0 1 1-1Zm3 5h6M9 12h6M9 16h3',
  clock:   'M12 7v5l3 2m6-2a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
  money:   'M12 2v20m5-16.5c0-1.4-2.2-2.5-5-2.5s-5 1.1-5 2.5S9.2 8 12 8s5 1.1 5 2.5-2.2 2.5-5 2.5-5-1.1-5-2.5',
  key:     'M15 7a4 4 0 1 1-3.5 5.9L7 17.4V21H3v-4l6.1-6.1A4 4 0 0 1 15 7Z',
  door:    'M6 21V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v17M3 21h18M14.5 12h.01',
  box:     'M3 8.5 12 4l9 4.5v7L12 20l-9-4.5v-7Zm0 0 9 4.5m0 0 9-4.5m-9 4.5V20',
  arrows:  'M7 4v12m0 0-3-3m3 3 3-3M17 20V8m0 0-3 3m3-3 3 3',
  bolt:    'M13 3 4 14h7l-1 7 9-11h-7l1-7Z',
  chat:    'M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12Z',
  book:    'M5 4h11a3 3 0 0 1 3 3v13H8a3 3 0 0 1-3-3V4Zm0 13a3 3 0 0 1 3-3h11',
  brush:   'M9.5 14.5 18.3 5.7a2 2 0 0 0-2.9-2.9l-8.7 8.9M9.5 14.5c-.6 2.8-2.7 5.5-6.5 5.5 0-3.2 1-5.9 3.7-8.3l2.8 2.8',
  inbox:   'M3 13h4l2 3h6l2-3h4M5.5 5h13l2.5 8v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-6l2.5-8Z',
}

export const NAV: NavItem[] = [
  // ── Finance: everything an admin does. Sections read in the order the month is
  //    actually worked — bring the data in, agree the terms, check it, then the
  //    practice's own money, then the things you set once.
  { module: 'finance', section: 'Overview',  to: '/',                      label: 'Overview',              roles: ['admin','data_admin'], icon: ICON.home },

  { module: 'finance', section: 'Payroll',   to: '/upload-report',         label: 'Upload report',         roles: ['data_admin'],         icon: ICON.upload },
  { module: 'finance', section: 'Payroll',   to: '/payslips',              label: 'Payslips',              roles: ['data_admin'],         icon: ICON.card },
  { module: 'finance', section: 'Payroll',   to: '/summary',               label: 'Summary',               roles: ['data_admin'],         icon: ICON.chart },
  { module: 'finance', section: 'Payroll',   to: '/payment-map',           label: 'Payment map',           roles: ['admin','data_admin'], icon: ICON.money },
  { module: 'finance', section: 'Payroll',   to: '/retention-calculation', label: 'Retention calculation', roles: ['admin','data_admin'], icon: ICON.percent },
  // Clinicians paid for their time rather than a share of what they bill. The hours are typed.
  { module: 'finance', section: 'Payroll',   to: '/hourly',                label: 'Hourly pay',            roles: ['admin','data_admin'], icon: ICON.clock },

  { module: 'finance', section: 'Ledger',    to: '/bills',                 label: 'Supplier bills',        roles: ['admin','data_admin'], icon: ICON.receipt },
  { module: 'finance', section: 'Ledger',    to: '/refunds',               label: 'Refunds',               roles: ['admin','data_admin'], icon: ICON.percent },
  { module: 'finance', section: 'Ledger',    to: '/debtors',               label: 'Debtors',               roles: ['admin','data_admin'], icon: ICON.money },

  { module: 'finance', section: 'Terms',     to: '/commission-management', label: 'Commission rates',      roles: ['admin','data_admin'], icon: ICON.percent },
  { module: 'finance', section: 'Terms',     to: '/retention-management',  label: 'Retention',             roles: ['admin','data_admin'], icon: ICON.sliders },
  { module: 'finance', section: 'Terms',     to: '/plan-fee-rates',        label: 'Plan fee rates',        roles: ['admin','data_admin'], icon: ICON.sliders },
  // Which plans and treatments earn a plan fee. Next to the rates on purpose: one page
  // says how much, the other says what for, and people arrive looking for either.
  { module: 'finance', section: 'Terms',     to: '/practice-plans',        label: 'Practice plans',        roles: ['admin','data_admin'], icon: ICON.card },
  { module: 'finance', section: 'Terms',     to: '/uplift-downlift',       label: 'Uplift / downlift',     roles: ['admin','data_admin'], icon: ICON.sliders },
  { module: 'finance', section: 'Terms',     to: '/charges',               label: 'Charges control',       roles: ['admin','data_admin'], icon: ICON.sliders },

  { module: 'finance', section: 'People',    to: '/add-practitioner',      label: 'Add practitioner',      roles: ['data_admin'],         icon: ICON.users },
  // ── Team: practice managers and CEOs (pages/Team.tsx, pages/TeamMember.tsx).
  { module: 'team', section: 'Team', to: '/team',     label: 'The team',        roles: ['data_admin'], accountRoles: ['practice_manager', 'ceo'], icon: ICON.users },
  { module: 'team', section: 'Team', to: '/team/add', label: 'Add a clinician', roles: ['data_admin'], accountRoles: ['practice_manager', 'ceo'], icon: ICON.user },

  { module: 'finance', section: 'Settings',  to: '/admin-settings',        label: 'Email settings',        roles: ['admin'],              icon: ICON.cog },
  { module: 'finance', section: 'Settings',  to: '/service-charges',       label: 'Service charges',       roles: ['admin'],              icon: ICON.cog },
  { module: 'finance', section: 'Settings',  to: '/practitioner-earnings', label: 'Practitioner earnings', roles: ['admin'],              icon: ICON.cog },
  { module: 'finance', section: 'Settings',  to: '/config',                label: 'Configuration',         roles: ['admin'],              icon: ICON.cog },
  { module: 'finance', section: 'Settings',  to: '/reset-password',        label: 'Reset password',        roles: ['admin','data_admin'], icon: ICON.key },

  // ── Appointments. The diary you read, and the diary you put someone into.
  //    Booking is in this module rather than its own: reading the day and filling a
  //    gap in it are the same job, and a badge for one page is furniture.
  { module: 'appointments', section: 'Diary',   to: '/appointments',      label: 'Appointments',          roles: ['admin','data_admin'], icon: ICON.chart },
  { module: 'appointments', section: 'Booking', to: '/booking/openings',  label: 'Open space',            roles: ['admin','data_admin'], icon: ICON.door },
  /*
   * "Book a patient" and "Requests" both belonged to a booking that ended in Lynqpod. It
   * ends in Dentally now: a patient books and it goes straight into the diary, and reception
   * books in Dentally itself rather than in a second place that then has to be copied over.
   * The pages are still routed, so a link to one works; neither is in the menu. Requests
   * puts itself back the moment something is waiting for a person (components/Layout.tsx).
   */
  { module: 'appointments', section: 'Booking', to: '/booking/setup',     label: 'Setup',                 roles: ['admin','data_admin'], icon: ICON.cog },

  // The form the patient fills in before they arrive. It sits here rather than under
  // Patients because of WHO opens it and WHEN: reception sends it off the back of a
  // booking, in the same sitting, and having to change module between making the
  // appointment and sending the form was a switch for no reason.
  /*
   * Pre-visit forms left the menu on 2026-10-10: nothing had ever been sent and no field had
   * ever been configured on either live practice, so it was a section of the product that only
   * cost a reader time. The ROUTE still resolves (App.tsx), so a bookmark does not 404, and the
   * patient-facing form at /f/:token is untouched: that is a different thing with a different
   * audience, and taking a menu item away must not quietly disable a patient's link.
   */

  // ── Rota. Two pages, because there are two questions and they are asked at
  //    different grains: the DAY is by the clock, where a nurse belongs to one
  //    appointment; the WEEK is by half-days, which is how a room is booked. It
  //    was briefly four pages showing the same day three ways, which is how you
  //    end up unable to find anything. Setup holds what the auto-fill reaches for.
  { module: 'rota',    section: 'Rota',     to: '/rota',                  label: 'The day',               roles: ['admin','data_admin'], icon: ICON.clock },
  { module: 'rota',    section: 'Rota',     to: '/rota/week',             label: 'The week',              roles: ['admin','data_admin'], icon: ICON.door },
  { module: 'rota',    section: 'Rota',     to: '/rota/setup',            label: 'Setup',                 roles: ['admin','data_admin'], icon: ICON.cog },

  // ── Marketing ───────────────────────────────────────────────────────────
  // Restored beside growth: that page is what the practice knows today, this one is for
  // the day it advertises. Judged on appointments, not on form fills.
  // ── Reports ─────────────────────────────────────────────────────────────
  { module: 'reports', section: 'Reports',   to: '/marketing/ad-patients', label: 'Where patients came from', roles: ['admin','data_admin'], icon: ICON.chart },
  { module: 'reports', section: 'Reports',   to: '/marketing/origins',     label: 'Patient map',              roles: ['admin','data_admin'], icon: ICON.users },
  { module: 'reports', section: 'Reports',   to: '/marketing',             label: 'Patient growth',           roles: ['admin','data_admin'], icon: ICON.chart },
  { module: 'reports', section: 'Reports',   to: '/marketing/funnel',      label: 'Booking funnel',           roles: ['admin','data_admin'], icon: ICON.percent },

  { module: 'marketing', section: 'Acquisition', to: '/marketing/ads',  label: 'Ads',                      roles: ['admin','data_admin'], icon: ICON.percent },
  // Where visits to the booking page stop, by source.
  // How the booking page looks: layout, colour, words, photo (pages/MarketingPageDesign.tsx).
  { module: 'marketing', section: 'Acquisition', to: '/marketing/page-design', label: 'Page design',       roles: ['admin','data_admin'], icon: ICON.brush },
  // Which assistants read this practice, and which of them sent a patient.
  { module: 'marketing', section: 'Acquisition', to: '/marketing/discovery',   label: 'Discovery',         roles: ['admin','data_admin'], icon: ICON.chart },
  // Where the patients actually live, on a map. Postcode districts, never addresses.
  // The other door: ask people something without making them pick a slot first.
  { module: 'marketing', section: 'Acquisition', to: '/forms',                 label: 'Forms',             roles: ['admin','data_admin'], icon: ICON.inbox },
  { module: 'marketing', section: 'Acquisition', to: '/forms/enquiries',       label: 'Enquiries',         roles: ['admin','data_admin'], icon: ICON.users },
  // Offers is off the menu for now (24 September 2026), at the owner's request. The page,
  // its route (/marketing/offers) and everything it drives are untouched: an offer already
  // saved still shows where it was set to show. Put this line back to bring it out again.
  // { module: 'marketing', section: 'Offers',   to: '/marketing/offers',    label: 'Offers',            roles: ['admin','data_admin'], icon: ICON.percent },
  // Talking to the patients the practice already has: who (segments), then what (campaigns).
  { module: 'marketing', section: 'Outreach',    to: '/marketing/segments',  label: 'Segments',          roles: ['admin','data_admin'], icon: ICON.users },
  { module: 'marketing', section: 'Outreach',    to: '/marketing/campaigns', label: 'Campaigns',         roles: ['admin','data_admin'], icon: ICON.inbox },


  // ── Inventory. Overview first, because "what is selling" is the question somebody
  //    opens this with; Stock is the working screen; Sold is Dentally's own record of
  //    who bought what; Movements is where you go when the count and the cupboard
  //    disagree and somebody has to find out why.
  { module: 'inventory', section: 'Overview',        to: '/inventory',       label: 'Overview',          roles: ['admin','data_admin'], icon: ICON.chart },
  { module: 'inventory', section: 'The shelf',       to: '/inventory/stock', label: 'Stock',             roles: ['admin','data_admin'], icon: ICON.box },
  { module: 'inventory', section: 'The shelf',       to: '/inventory/stocktake', label: 'Stocktake',     roles: ['admin','data_admin'], icon: ICON.sliders },
  { module: 'inventory', section: 'The shelf',       to: '/inventory/sold',  label: 'Sold',              roles: ['admin','data_admin'], icon: ICON.money },
  { module: 'inventory', section: 'The shelf',       to: '/inventory/moves', label: 'Movements',         roles: ['admin','data_admin'], icon: ICON.arrows },
  { module: 'automations', section: 'Rules',      to: '/automations',         label: 'Automations',       roles: ['admin','data_admin'], icon: ICON.bolt },
  { module: 'automations', section: 'Your work',  to: '/automations/inbox',   label: 'Inbox',             roles: ['admin','data_admin'], icon: ICON.inbox },
  { module: 'automations', section: 'Rules',      to: '/automations/history', label: 'History',           roles: ['admin','data_admin'], icon: ICON.clock },
  // "Who buys what" lives in Inventory (2026-09-15) but is still sold as Research: it shows only
  // where the practice has that module (`needs`), and the server still gates it on 'research'.
  { module: 'inventory', section: 'Research', to: '/research/sundries', label: 'Who buys what', roles: ['admin','data_admin'], icon: ICON.chart, needs: 'research' },
  { module: 'recordings', section: 'Sessions', to: '/recordings',       label: 'Recordings',     roles: ['admin','data_admin'], icon: ICON.users },
  { module: 'recordings', section: 'Sessions', to: '/recordings/setup', label: 'Setup',          roles: ['admin','data_admin'], icon: ICON.cog },
  { module: 'appointments', section: 'Chair time', to: '/gaps',          label: 'Gaps',         roles: ['admin','data_admin'], icon: ICON.clock },
  { module: 'appointments', section: 'Chair time', to: '/gaps/waitlist', label: 'Waiting list', roles: ['admin','data_admin'], icon: ICON.users },
  { module: 'appointments', section: 'Chair time', to: '/gaps/at-risk',  label: 'At risk',      roles: ['admin','data_admin'], icon: ICON.bolt },
  { module: 'chat', section: 'Chat',      to: '/chat',         label: 'Conversations',   roles: ['admin','data_admin'], icon: ICON.chat },
  /*
   * WhatsApp stopped being a module of its own on 2026-10-10 and moved in here.
   *
   * It was never a separate job. A patient who messages the practice on WhatsApp and one who
   * types into the website widget are the same person asking the same thing, and splitting them
   * into two modules meant two inboxes, two places to look, and a practice deciding which to
   * check first.
   *
   * ⚠️ `needs` KEEPS IT SOLD SEPARATELY. The entitlement is unchanged, so a practice that has
   * not bought WhatsApp still does not see these three; they simply live under Chat for the
   * practices that have. The server's gating is untouched.
   */
  /* Not "Conversations": that label already belongs to the inbox above, and a menu with the
     same word twice makes a reader check which one they are on. */
  { module: 'chat', section: 'WhatsApp', to: '/whatsapp',           label: 'WhatsApp inbox', roles: ['admin','data_admin'], icon: ICON.chat,  needs: 'whatsapp' },
  { module: 'chat', section: 'WhatsApp', to: '/whatsapp/reminders', label: 'Reminders',     roles: ['admin','data_admin'], icon: ICON.clock, needs: 'whatsapp' },
  { module: 'chat', section: 'WhatsApp', to: '/whatsapp/setup',     label: 'Setup',         roles: ['admin','data_admin'], icon: ICON.cog,   needs: 'whatsapp' },
  { module: 'chat', section: 'The bot',   to: '/chat/answers', label: 'Bot answers',     roles: ['admin','data_admin'], icon: ICON.book },
  { module: 'chat', section: 'The bot',   to: '/chat/treatments', label: 'Treatments',   roles: ['admin','data_admin'], icon: ICON.box },
  { module: 'chat', section: 'The bot',   to: '/chat/flows',   label: 'Flows',           roles: ['admin','data_admin'], icon: ICON.arrows },

  // ── Platform ────────────────────────────────────────────────────────────
  { module: 'platform', section: 'Estate', to: '/platform/accounts', label: 'Practices',      roles: ['platform_admin'], icon: ICON.users },
  { module: 'platform', section: 'Estate', to: '/platform/billing',  label: 'Subscriptions', roles: ['platform_admin'], icon: ICON.money },
  { module: 'platform', section: 'Onboarding', to: '/platform/new-client', label: 'New client', roles: ['platform_admin'], icon: ICON.user },
  { module: 'platform', section: 'Onboarding', to: '/platform/packages',   label: 'Packages',   roles: ['platform_admin'], icon: ICON.box },

  // ── A practitioner's own ────────────────────────────────────────────────
  { module: 'mine',    section: 'Overview',  to: '/my-month',              label: 'Dashboard',             roles: ['practitioner'],       icon: ICON.home },
  { module: 'mine',    section: 'Overview',  to: '/my-payslips',           label: 'My payslips',           roles: ['practitioner'],       icon: ICON.invoice },
  { module: 'mine',    section: 'Overview',  to: '/my-profile',            label: 'My profile',            roles: ['practitioner'],       icon: ICON.user },
  { module: 'mine',    section: 'My diary',  to: '/booking',               label: 'My diary',              roles: ['practitioner'],       icon: ICON.clock },
  { module: 'mine',    section: 'My diary',  to: '/booking/openings',      label: 'My open space',         roles: ['practitioner'],       icon: ICON.door },
  { module: 'mine',    section: 'My diary',  to: '/booking/requests',      label: 'My requests',           roles: ['practitioner'],       icon: ICON.receipt },
]

/**
 * Whether this practice has bought a module.
 *
 * Hiding a module is a COURTESY, not a control: the server refuses the endpoints
 * regardless (see services/entitlements.service.js). This exists so a practice is
 * not shown a door that will not open, and `undefined` means "not loaded yet",
 * which shows everything rather than flashing an empty sidebar on every page load.
 */
export type Entitlements = Record<string, boolean> | undefined

/**
 * Modules put away for now, for every practice (2026-09-13: Automations, at the owner's
 * word). Their pages and the server are untouched: take the key out of this list and they
 * are back. A practice with automations switched on would keep running them unseen, so
 * check none are on before adding one here.
 */
export const HIDDEN_MODULES: ModuleKey[] = ['automations']
export const isHidden = (key: ModuleKey) => HIDDEN_MODULES.includes(key)

const bought = (key: ModuleKey, enabled: Entitlements) => {
  if (isHidden(key)) return false
  // `mine` is a practitioner's own pay and `platform` is ours. Neither is sold.
  if (key === 'mine' || key === 'platform') return true
  if (!enabled) return true
  return enabled[key] !== false
}

/** The modules this role can actually reach — one with no pages is not offered. */
/** An account role limit, if there is one, that this account meets. */
const forAccount = (x: { accountRoles?: AccountRole[] }, accountRole?: AccountRole) => !x.accountRoles || (!!accountRole && x.accountRoles.includes(accountRole))

export function modulesFor(role: Role | undefined, enabled?: Entitlements, accountRole?: AccountRole): ModuleDef[] {
  if (!role) return []
  return MODULES.filter((m) =>
    m.roles.includes(role) && forAccount(m, accountRole)
    && bought(m.key, enabled)
    && NAV.some((n) => n.module === m.key && n.roles.includes(role) && forAccount(n, accountRole)))
}

export function navFor(role: Role | undefined, module?: ModuleKey, enabled?: Entitlements, accountRole?: AccountRole) {
  if (!role) return []
  return NAV.filter((n) =>
    n.roles.includes(role) && forAccount(n, accountRole) && bought(n.module, enabled) && (!n.needs || bought(n.needs, enabled)) && (!module || n.module === module))
}

/** Which module a path belongs to, so the switcher follows the page you land on. */
export function moduleOf(pathname: string): ModuleKey | undefined {
  const exact = NAV.find((n) => n.to === pathname)
  if (exact) return exact.module
  // /booking/295612 is a page per clinician and has no nav entry of its own. Without
  // this the switcher finds nothing and the sidebar empties itself on that page.
  return NAV.filter((n) => n.to !== '/' && pathname.startsWith(`${n.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0]?.module
}
